import { useMutation } from '@apollo/client'
import { ButtonLink } from '@designsforhealth/dfh-react-components'
import { ButtonLinkProps } from '@designsforhealth/dfh-react-components/dist/components/Button/ButtonLink'
import { CircularProgress } from '@material-ui/core'
import React, { FC, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { DeleteCustomerCardDocument, TokenBaseCardsDocument } from '../../../../graphql/magento'
import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../../layouts/context'
import { SelectableTokenBaseCard } from '../../../../lib/cybersource/cards'
import CreditCardLogo from '../../../CreditCardLogo'

interface CreditCardProps {
  className?: string
  creditCard: SelectableTokenBaseCard
}

const StyledCreditCardLogo = styled(CreditCardLogo)`
  font-size: 22px;
`

const CardNumber = styled.div`
  &:before {
    content: 'xxxx-xxxx-xxxx-';
  }
  margin-top: 10px;
`

const CardExpiration = styled.div`
  font-size: 15px;
  margin-top: 10px;
`

const CardAddress = styled.address`
  font-size: 15px;
  font-style: normal;
  margin-top: 10px;
`

const ConfirmButtonLink = styled(ButtonLink)<ButtonLinkProps & { $confirm?: boolean }>`
  ${({ $confirm }) => $confirm && 'font-style: italic;'};
`

const CreditCard: FC<CreditCardProps> = ({ className, creditCard }) => {
  const token = useContext(TokenContext)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [deleteCustomerCard, { error: errorDeleteCard, loading: loadingDeleteCard }] = useMutation(
    DeleteCustomerCardDocument,
    {
      context: { token },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: TokenBaseCardsDocument,
          context: { token },
        },
      ],
    },
  )

  useLogAndCaptureError(errorDeleteCard)

  const onDeleteCard = () => {
    if (!creditCard?.hash) {
      return
    }
    deleteCustomerCard({ variables: { hash: creditCard?.hash } })
  }

  useEffect(() => {
    if (!confirmDelete) {
      return
    }
    const timeout = setTimeout(() => {
      setConfirmDelete(false)
    }, 30000)
    return () => clearTimeout(timeout)
  }, [confirmDelete])

  return (
    <div className={`${className} credit-card`}>
      <div className="credit-card-details">
        <StyledCreditCardLogo ccType={creditCard.additional.cc_type} />
        <CardNumber>{creditCard.additional.cc_last4}</CardNumber>
        <CardExpiration>
          Exp. {creditCard.additional.cc_exp_month}/{creditCard.additional.cc_exp_year}
        </CardExpiration>
        <CardAddress>
          <div>
            {creditCard.address.firstname} {creditCard.address.lastname}
          </div>
          {creditCard.address.street?.map((street, index) => <div key={index}>{street}</div>)}
          <div>
            {creditCard.address.city}, {creditCard.address.region?.region_code}{' '}
            {creditCard.address.postcode}
          </div>
        </CardAddress>
      </div>
      <ConfirmButtonLink
        disabled={loadingDeleteCard}
        onClick={confirmDelete ? onDeleteCard : () => setConfirmDelete(true)}
        underline
        $confirm={confirmDelete}
      >
        {loadingDeleteCard ? (
          <CircularProgress size={24} color="inherit" />
        ) : confirmDelete ? (
          'Confirm Delete'
        ) : (
          'Delete'
        )}
      </ConfirmButtonLink>
    </div>
  )
}

const StyledCreditCard = styled(CreditCard)(({ theme }) => ({
  border: `1px solid ${theme.colors.coolGray.cool450}`,
  padding: '10px 15px',
  'a, a:visited': {
    color: theme.colors.grayscale.black,
  },
  'a:first-of-type': {
    marginRight: '22px',
  },
  '.credit-card-details': {
    lineHeight: '18px',
    marginBottom: '12px',
    div: {
      marginBottom: '3px',
    },
  },
}))

export default StyledCreditCard
